import {
    ArrowBack as ArrowBackIcon,
    ChatBubbleOutline as CommentIcon,
    Subject as SubjectIcon,
    ThumbUpOutlined as LikeIcon
} from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const HomeworkDetails = () => {
    const navigate = useNavigate();
    const { submissionid } = useParams();
    const [submission, setSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const apiURL = process.env.REACT_APP_API;

    useEffect(() => {
        fetchDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionid]);

    const fetchDetails = async () => {
        if (!submissionid) {
            setNotFound(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${apiURL}/public/homework/details`, {
                params: { submissionid }
            });

            if (response.data.status === true) {
                setSubmission(response.data.data);
            } else {
                setNotFound(true);
            }
        } catch (error) {
            console.error('Error fetching home work details:', error);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/homework');
    };

    const formatDate = (d) => {
        if (!d) return '';
        return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size={48} />
                    <Typography variant="body1" color="text.secondary">
                        Loading home work...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (notFound || !submission) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Alert severity="error" sx={{ maxWidth: 500 }}>
                    This home work submission is not available.
                </Alert>
            </Box>
        );
    }

    const comments = submission.comments || [];

    return (
        <Box sx={{ py: 4 }}>
            <Container maxWidth="lg">
                <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
                    Back to Home Work
                </Button>

                <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
                    <Box sx={{ p: 4 }}>
                        <Grid container spacing={4}>
                            {/* Media + Caption */}
                            <Grid item xs={12} md={7}>
                                <Box sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.100', mb: 3 }}>
                                    {submission.sub_video ? (
                                        <video
                                            src={submission.sub_video}
                                            controls
                                            controlsList="nodownload noremoteplayback"
                                            disablePictureInPicture
                                            style={{ width: '100%', maxHeight: 480, display: 'block' }}
                                        />
                                    ) : submission.sub_image ? (
                                        <img src={submission.sub_image} alt={submission.sub_title || 'Home work submission'} style={{ width: '100%', maxHeight: 480, objectFit: 'contain', display: 'block' }} />
                                    ) : (
                                        <Box display="flex" alignItems="center" justifyContent="center" minHeight={300}>
                                            <Typography color="text.secondary">No media available</Typography>
                                        </Box>
                                    )}
                                </Box>

                                {submission.sub_title && (
                                    <Typography variant="h5" fontWeight={700} gutterBottom>
                                        {submission.sub_title}
                                    </Typography>
                                )}
                                {submission.sub_caption && (
                                    <Typography variant="body1" color="text.secondary" paragraph>
                                        {submission.sub_caption}
                                    </Typography>
                                )}

                                <Stack direction="row" spacing={3} mt={2}>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <LikeIcon color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            {submission.like_count || 0} likes
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <CommentIcon color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            {submission.comment_count || 0} comments
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Grid>

                            {/* Side Info */}
                            <Grid item xs={12} md={5}>
                                <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
                                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                        <Avatar src={submission.student_dp} sx={{ width: 48, height: 48 }}>
                                            {submission.student_name?.charAt(0)?.toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                {submission.student_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Submitted {formatDate(submission.sub_created_at)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Divider sx={{ my: 2 }} />
                                    <Chip icon={<SubjectIcon />} label={submission.subject_name} color="primary" variant="outlined" />
                                </Paper>

                                <Paper variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                        Comments ({submission.comment_count || 0})
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    {comments.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No comments yet.
                                        </Typography>
                                    ) : (
                                        <Stack spacing={2}>
                                            {comments.map((comment, index) => (
                                                <Stack key={comment.commentid || index} direction="row" spacing={1.5}>
                                                    <Avatar src={comment.student_dp} sx={{ width: 32, height: 32 }}>
                                                        {comment.student_name?.charAt(0)?.toUpperCase()}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {comment.student_name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {comment.comment_text}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatDate(comment.created_at)}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default HomeworkDetails;
